import type { FC } from 'react';
import React, { useState, useContext, useCallback, Fragment } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { useMutation } from '@apollo/react-hooks';
import type { ExecutionResult } from '@apollo/react-common';
import type { ApolloError } from 'apollo-client';

import AkModalDialog, {
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalBody,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import ButtonGroup from '@atlaskit/button/button-group';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Flex, Stack } from '@atlaskit/primitives';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { token } from '@atlaskit/tokens';
import { useFlags, FlagsProvider } from '@atlaskit/flag';
import { R400 } from '@atlaskit/theme/colors';

import {
	CONVERT_PAGE_TO_FOLDER_EXPERIENCE,
	ExperienceTrackerContext,
	stopExperienceOnError,
} from '@confluence/experience-tracker';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { ScreenEvent } from '@confluence/analytics';
import { FOLDER_VIEW } from '@confluence/named-routes';
import { ErrorDisplay, isExpectedApolloError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';

import { ConvertPageToFolderMutation } from './ConvertPageToFolderMutation.graphql';
import type {
	ConvertPageToFolderMutation as ConvertPageToFolderMutationType,
	ConvertPageToFolderMutationVariables,
} from './__types__/ConvertPageToFolderMutation';

const i18n = defineMessages({
	dialogHeader: {
		id: 'folder.convert-page-to-folder.dialog.header',
		description: 'Heading for convert page to folder dialog',
		defaultMessage: 'Convert this page into a folder?',
	},
	dialogDescription: {
		id: 'folder.convert-page-to-folder.dialog.description',
		description: 'Description for convert page to folder dialog',
		defaultMessage:
			'We’ll archive the original page, so you don’t need to worry about losing your work.',
	},
	cancel: {
		id: 'folder.convert-page-to-folder.dialog.cancel',
		defaultMessage: 'Cancel',
		description: 'Cancel button text for convert page to folder dialog',
	},
	dialogSubmitText: {
		id: 'folder.convert-page-to-folder.dialog.submit',
		defaultMessage: 'Convert',
		description: 'Text for completing conversion of page to folder',
	},
	// TODO: Need to implement success flag. This has to be done inside the parent
	// component ContentTools.tsx, or the flag will disappear when dialog closes (DISCO-2505)
	successFlagTitle: {
		id: 'folder.convert-page-to-folder.flags.success-message.title',
		defaultMessage: 'Conversion complete',
		description: 'Flag title shown after conversion of page to folder succeeds',
	},
	successFlagDescription: {
		id: 'folder.convert-page-to-folder.flags.success-message.description',
		defaultMessage: 'We’ve finished converting “{title}” to a folder',
		description: 'Flag description shown after conversion of page to folder succeeds',
	},
	defaultErrorFlagTitle: {
		id: 'folder.convert-page-to-folder.flags.error-message.title',
		defaultMessage: 'We couldn’t convert the page to a folder',
		description: 'Flag title shown after conversion of page to folder failed',
	},
	defaultErrorFlagDescription: {
		id: 'folder.convert-page-to-folder.flags.error-message.description',
		defaultMessage: 'Something went wrong, and we were unable to convert the page to a folder.',
		description: 'Flag description shown after conversion of page to folder failed',
	},
	defaultErrorFlagAction: {
		id: 'folder.convert-page-to-folder.flags.error-message.action',
		defaultMessage: 'Try again',
		description:
			'Flag action button shown after conversion of page to folder failed. Clicking on this will retry the folder conversion operation',
	},
});

const COMPONENT_NAME = 'ConvertPageToFolderDialogModal';
const source = 'VIEW_PAGE';

type ConvertPageToFolderDialogProps = {
	spaceKey: string;
	contentId: string;
	onClose: () => void;
};

const ConvertPageToFolderDialogComponent: FC<ConvertPageToFolderDialogProps> = ({
	spaceKey,
	contentId,
	onClose,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { push } = useRouteActions();
	const { showFlag } = useFlags();

	const [isConverting, setIsConverting] = useState(false);

	const [convertPageToFolderMutation, { error }] = useMutation<
		ConvertPageToFolderMutationType,
		ConvertPageToFolderMutationVariables
	>(ConvertPageToFolderMutation);

	const handleOnClose = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'clicked',
				actionSubject: COMPONENT_NAME,
				actionSubjectId: `${COMPONENT_NAME}CancelButton`,
				source,
			},
		}).fire();
		onClose();
	}, [createAnalyticsEvent, onClose]);

	const convertPageToFolderSuccess = (data: ExecutionResult<ConvertPageToFolderMutationType>) => {
		setIsConverting(false);
		experienceTracker.succeed({
			name: CONVERT_PAGE_TO_FOLDER_EXPERIENCE,
		});

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: COMPONENT_NAME,
				action: 'success',
				attributes: undefined,
				source,
			},
		}).fire();

		const contentId = data?.data?.convertToFolder?.content?.id;
		if (contentId) {
			const newlyCreatedFolderUrl = FOLDER_VIEW.toUrl({ spaceKey, contentId });
			newlyCreatedFolderUrl && push(newlyCreatedFolderUrl);
		}

		onClose();
	};

	const convertPageToFolderFailure = (error: ApolloError) => {
		if (isExpectedApolloError(error)) {
			markErrorAsHandled(error);
			experienceTracker.abort({
				name: CONVERT_PAGE_TO_FOLDER_EXPERIENCE,
				reason: 'Expected GQL error',
			});
		} else {
			stopExperienceOnError(CONVERT_PAGE_TO_FOLDER_EXPERIENCE, error);
		}

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: COMPONENT_NAME,
				action: 'failed',
				attributes: { error },
				actionSubjectId: `${COMPONENT_NAME}ConvertButton`,
				source,
			},
		}).fire();

		showFlag({
			title: formatMessage(i18n.defaultErrorFlagTitle),
			description: formatMessage(i18n.defaultErrorFlagDescription),
			icon: <ErrorIcon label="" primaryColor={token('color.icon.danger', R400)} />,
			isAutoDismiss: true,
			actions: [
				{
					content: formatMessage(i18n.defaultErrorFlagAction),
					onClick: handlePageToFolderConversion,
				},
			],
		});
	};

	const handlePageToFolderConversion = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: COMPONENT_NAME,
				actionSubjectId: `${COMPONENT_NAME}ConvertButton`,
				source,
			},
		}).fire();

		setIsConverting(true);
		experienceTracker.start({
			name: CONVERT_PAGE_TO_FOLDER_EXPERIENCE,
		});

		convertPageToFolderMutation({
			variables: {
				id: contentId,
			},
		})
			.then((data) => {
				convertPageToFolderSuccess(data);
			})
			.catch((error) => {
				convertPageToFolderFailure(error);
				setIsConverting(false);
			});
	};

	return (
		<Fragment>
			<AkModalDialog
				width="450px"
				key="ConvertPageToFolderDialog"
				onClose={handleOnClose}
				shouldScrollInViewport
			>
				<ModalBody>
					<ModalHeader>
						<ModalTitle>
							<FormattedMessage {...i18n.dialogHeader} />
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<FormattedMessage {...i18n.dialogDescription} />
					</ModalBody>
					<ModalFooter>
						<Stack space="space.200">
							<Flex justifyContent="end">
								<ButtonGroup>
									<Button onClick={handleOnClose} appearance="subtle">
										<FormattedMessage {...i18n.cancel} />
									</Button>

									<Button
										onClick={handlePageToFolderConversion}
										appearance="primary"
										isLoading={isConverting}
									>
										<FormattedMessage {...i18n.dialogSubmitText} />
									</Button>
								</ButtonGroup>
							</Flex>
						</Stack>
					</ModalFooter>
				</ModalBody>
			</AkModalDialog>
			<ScreenEvent
				name="convertPageToFolderDialogModalScreen"
				id="convertPageToFolderModalScreenEvent"
			/>
			{error && <ErrorDisplay error={error} />}
		</Fragment>
	);
};

export const ConvertPageToFolderDialog = (props: ConvertPageToFolderDialogProps) => (
	<FlagsProvider>
		<ConvertPageToFolderDialogComponent {...props} />
	</FlagsProvider>
);
